import React, { JSX, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  CustomItemRegistry,
  DateDisplay,
  Overlay,
  OverlayContainer,
  Price,
  Summary,
  useAppService,
  useBookingService,
  useConfig,
  useTranslation
} from '@ibe/components';
import {
  ApiBookedItem,
  ApiBooking,
  ApiComponentType,
  ApiDirection,
  ApiFlightItem,
  ApiFlightLeg,
  ApiItemType,
  ApiPrice,
  ApiPriceFromJSON,
  ApiPriceModifierType,
  ApiTraveler
} from '@ibe/api';
import { BookingService, LoggerFactory, SessionStorage } from '@ibe/services';
import fallback from '../../../../Translations/generated/Checkout.de.json';
import Keys from '../../../../Translations/generated/Checkout.de.json.keys';
import fallbackSummary from '../../../../Translations/generated/summary.de.json';
import KeysSummary from '../../../../Translations/generated/summary.de.json.keys';
import PriceOverviewHeader from './PriceOverviewHeader';
import CheckoutStore, { ProductIBEMode } from '../../CheckoutStore';
import PromotionCode, {
  isValidPromotionCode,
  PromotionCodeType
} from '../../../../Components/PromotionCode';
import { getTotalDiscount, validPromotionCodeStorage } from '../../../Util/utils';
import useThgConfig from '../../../../Hooks/useThgConfig';
import CustomPerTravelerSummaryItem, {
  BookedItemType
} from './CustomPerTravelerSummaryItem/CustomPerTravelerSummaryItem';
import {
  SelectedItemsType,
  useExtendedBookingForPriceOverview
} from '../ExtendedBooking/ContextAndProvider';
import { Col, Row } from 'reactstrap';
import { PackageTypes } from '../../../Products/ProductIBE';
import { NORMAL_GROUP } from '../../../../Util/constant';
import { summaryOverviewSessionKey } from '../../../../Config/sessionkeys';

const logger = LoggerFactory.get('SummaryContent');

const EmptyBusItem = (): JSX.Element => <></>;

const resolveBusStation = (
  storeDesc: string | null | undefined,
  selectedDesc: string | null | undefined
): string | undefined => storeDesc ?? selectedDesc ?? undefined;

const getBusStationRow = (
  isConfirmationPage: boolean | undefined,
  isBusPackage: boolean,
  busStationDescription: string | undefined
): string | null => (isConfirmationPage && isBusPackage ? busStationDescription ?? null : null);

const SummaryContent = observer(
  ({
    booking,
    productIBEMode,
    showPromotionCode,
    travelers,
    extendedBookingItems,
    isConfirmationPage,
    store,
    onTotalPriceChange
  }: {
    booking: ApiBooking;
    store: CheckoutStore;
    productIBEMode?: ProductIBEMode;
    showPromotionCode?: boolean;
    handleBookingError?: (errorMessage: string) => void;
    travelers?: ApiTraveler[];
    extendedBookingItems?: SelectedItemsType;
    isConfirmationPage?: boolean;
    onTotalPriceChange?: (a: number) => void;
  }): JSX.Element => {
    const bookedItems = booking?.bookedItems;
    const bookingDate = booking?.bookingDate;
    const { t } = useTranslation('Checkout', fallback);
    const { t: tSummary } = useTranslation('summary', fallbackSummary);
    const bs = useBookingService();
    const saveCollapsibles = summaryOverviewSession.get()?.collapsible?.expandedItemIndexes || [];
    const [openedCollapsibles, setOpenedCollapsibles] = useState<number[]>(saveCollapsibles);
    const [totalPricePerEachTraveler, setTotalPricePerEachTraveler] = useState<{
      [a: string]: number;
    }>({});
    const [promotionCode, setPromotionCode] = useState<PromotionCodeType>(undefined);
    const initialPromotionCode = productIBEMode?.promotionCode;
    const config = useConfig();
    const thgConfig = useThgConfig();
    const baseUrl = thgConfig.baseUrl || config.translationsUrl;

    const selectedItems = useExtendedBookingForPriceOverview();

    const isBusPackage =
      store.packageType === PackageTypes.PBUS ||
      sessionStorage.getItem('packageType') === PackageTypes.PBUS;

    const isFlightPackage =
      store.packageType === PackageTypes.PFLUG ||
      sessionStorage.getItem('packageType') === PackageTypes.PFLUG;

    const totalPrice = useMemo(() => {
      if (!extendedBookingItems || isConfirmationPage) {
        return booking.price?.finalPrice;
      }
      return Object.values(totalPricePerEachTraveler).reduce((acc, currentValue) => {
        return acc + currentValue;
      }, 0);
    }, [totalPricePerEachTraveler, extendedBookingItems, isConfirmationPage]);

    const currencyCode = booking.price?.currencyCode ?? bs.booking?.price.currencyCode ?? 'EUR';
    const sign =
      new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol'
      })
        .formatToParts(0)
        .find(p => p.type === 'currency')?.value ?? currencyCode;

    useEffect(() => {
      onTotalPriceChange && onTotalPriceChange(totalPrice);
    }, [totalPrice]);

    const applyPromotionCode = async (code: string): Promise<void> => {
      try {
        await bs.addPromoCodesToBooking([code]);
        const discount = getTotalDiscount(bs.booking?.travelers || [], bs.bookedItems);
        if (discount > 0) {
          const codeType = { code, absoluteValue: discount, sign: sign };
          setPromotionCode({ ...codeType });
          validPromotionCodeStorage.set({ ...codeType });
        }
      } catch (err) {
        logger.error(err);
        setPromotionCode(value => (value === 'ERROR' ? 'NONE' : 'ERROR'));
      }
    };

    useEffect(() => {
      (async () => {
        const sessionStorageCode = validPromotionCodeStorage.get();
        const promoCode =
          sessionStorageCode && sessionStorageCode.code
            ? sessionStorageCode.code
            : initialPromotionCode;

        if (showPromotionCode && !!promoCode) {
          await applyPromotionCode(promoCode);
        }
      })();
    }, [initialPromotionCode, showPromotionCode]);

    useEffect(() => {
      const discount = getTotalDiscount(bs.booking?.travelers || [], bs.bookedItems);
      if (discount > 0) {
        const oldCodeType = validPromotionCodeStorage.get();
        if (oldCodeType) {
          const codeType = { code: oldCodeType.code, absoluteValue: discount, sign: sign };
          setPromotionCode({ ...codeType });
          validPromotionCodeStorage.set({ ...codeType });
        }
      }
    }, [bs.bookedItems]);

    // FOR DESIGN REASON just take the first flight
    const filteredBookedItems = useMemo(() => {
      return filterBookedItems(bookedItems);
    }, [bookingDate, bookedItems.length]);

    const handlePromotionCodeSubmit = async (code: PromotionCodeType): Promise<void> => {
      if (isValidPromotionCode(code)) {
        await applyPromotionCode(code.code);
      }
    };

    const customPriceSummaryRendering = useCallback((item: ApiBookedItem, travelerId?: string):
      | ApiPrice
      | undefined => {
      if (!travelerId) return undefined;
      const discountedPrice = item.priceByPersonId[travelerId].modifiers.find(
        mod => mod.type === ApiPriceModifierType.DISCOUNT && item.itemType !== ApiItemType.PACKAGE
      );
      if (!!discountedPrice) {
        return {
          ...item.priceByPersonId[travelerId],
          finalPrice:
            item.priceByPersonId[travelerId].finalPrice + Math.abs(discountedPrice?.absolute)
        };
      } else {
        return item.priceByPersonId[travelerId];
      }
    }, []);

    const showPlusMinus = (item: ApiBookedItem): boolean => {
      return item.itemType !== ApiItemType.HOTEL;
    };

    const getOpenedCollapsible = (prev: number[], travelerIdx: number) => {
      if (prev.includes(travelerIdx)) {
        return prev.filter(val => val !== travelerIdx);
      } else {
        return prev.concat([travelerIdx]);
      }
    };

    const onCollapseToggle = (travelerIdx: number) => {
      const newCollapseds = getOpenedCollapsible(openedCollapsibles, travelerIdx);
      setOpenedCollapsibles(newCollapseds);
      summaryOverviewSession.set({ collapsible: { expandedItemIndexes: newCollapseds } });
    };

    const customPerTravelerSummaryItem = (
      traveler: ApiTraveler,
      extBookedItems: BookedItemType[],
      travelerIdx: number
    ) => {
      const isOpen = openedCollapsibles.includes(travelerIdx);
      return (
        <CustomPerTravelerSummaryItem
          onCollapseToggle={onCollapseToggle}
          isOpen={isOpen}
          traveler={traveler}
          travelers={travelers}
          extendedBookingItems={extendedBookingItems}
          travelerIdx={travelerIdx}
          bookedItems={extBookedItems}
          onTotalPriceChange={(a: number) => getTotalPrice(a, traveler)}
          booking={booking}
          key={travelerIdx}
          isConfirmationPage={isConfirmationPage}
        />
      );
    };
    const getTotalPrice = (a: number, traveler: ApiTraveler) => {
      setTotalPricePerEachTraveler(prevState => {
        return { ...prevState, [traveler.id]: a };
      });
    };

    const insuranceBookedItem = bookedItems.find(
      b => ApiItemType.INSURANCE === b.itemType && ApiComponentType.OPTIONAL === b.componentType
    );

    const customLabelsFlightMapper = useCallback(
      (item: ApiBookedItem): string | undefined => {
        if (
          item.componentType === ApiComponentType.OPTIONAL &&
          item.itemType === ApiItemType.FLIGHT
        ) {
          return thgConfig.checkoutLabels?.railAndFly;
        }

        if (
          item.componentType === ApiComponentType.REQUIRED &&
          item.itemType === ApiItemType.FLIGHT
        ) {
          const flightItemIds = booking.bookedItems
            .filter(
              bookedItem =>
                bookedItem.itemType === ApiItemType.FLIGHT &&
                bookedItem.componentType === ApiComponentType.REQUIRED
            )
            .map(bitem => bitem.idParent);

          if (item.idParent !== flightItemIds[0]) return undefined;

          const outBoundFlights = booking.items
            .filter(
              flightItem =>
                flightItem.itemType === ApiItemType.FLIGHT &&
                (flightItem as ApiFlightItem).segment[0].direction === ApiDirection.OUTBOUND &&
                flightItemIds.includes(flightItem.id)
            )
            .sort(
              (a, b) =>
                new Date((a as ApiFlightItem).segment[0].departure).getTime() -
                new Date((b as ApiFlightItem).segment[0].departure).getTime()
            );

          const inBoundFlights = booking.items
            .filter(
              flightItem =>
                flightItem.itemType === ApiItemType.FLIGHT &&
                (flightItem as ApiFlightItem).segment[0].direction === ApiDirection.INBOUND &&
                flightItemIds.includes(flightItem.id)
            )
            .sort(
              (a, b) =>
                new Date((a as ApiFlightItem).segment[0].departure).getTime() -
                new Date((b as ApiFlightItem).segment[0].departure).getTime()
            );

          const outBoundFlight = outBoundFlights[0] as ApiFlightItem;
          const inBoundFlight = inBoundFlights[0] as ApiFlightItem;

          return `${outBoundFlight.segment[0]?.origin.description} (${outBoundFlight.segment[0]?.origin.code}) - ${inBoundFlight.segment[0]?.origin.description} (${inBoundFlight?.segment[0].origin.code})`;
        }
      },
      [booking.items]
    );

    const busStationDescription = resolveBusStation(
      store.busInformation?.busStationDescription,
      selectedItems?.busStation?.description
    );

    const customLabelsBusMapper = useCallback(
      (_item: ApiBookedItem): ReactNode => {
        return (
          <div className=" d-flex align-items-start justify-content-between flex-grow-1">
            <div className="text-muted iso__summary__description">{t(Keys.yourBusStop)}</div>
            <div>
              <span className="iso__summary__price__container">
                <span>{busStationDescription}</span>
              </span>
            </div>
          </div>
        );
      },
      [busStationDescription]
    );

    const initialInBoundFlight = useCallback(() => {
      const flightItemIds = booking.bookedItems
        .filter(
          bookedItem =>
            bookedItem.itemType === ApiItemType.FLIGHT &&
            bookedItem.componentType === ApiComponentType.REQUIRED
        )
        .map(bitem => bitem.idParent);

      const inBoundFlights = booking.items
        .filter(
          flightItem =>
            flightItem.itemType === ApiItemType.FLIGHT &&
            (flightItem as ApiFlightItem).segment[0].direction === ApiDirection.INBOUND &&
            flightItemIds.includes(flightItem.id)
        )
        .sort(
          (a, b) =>
            new Date((a as ApiFlightItem).segment[0].departure).getTime() -
            new Date((b as ApiFlightItem).segment[0].departure).getTime()
        ) as ApiFlightItem[];

      return inBoundFlights[0].segment[0].legs[0];
    }, []);

    const busStationRow = getBusStationRow(isConfirmationPage, isBusPackage, busStationDescription);

    return (
      <div className={'ibe__flight'}>
        <PriceOverviewHeader
          booking={booking}
          title={t(Keys.travelOverview)}
          busStationRow={busStationRow}
        />
        <section
          className="sticky-summary-box-summary"
          role="region"
          aria-label={`${t(Keys.travelOverview)} – ${t(Keys.summaryOverview)}`}
        >
          <Summary
            booking={booking}
            showPricePlusMinus={showPlusMinus}
            withIconColumn={false}
            showAdditionalInfo
            showTraveler={false}
            travelers={travelers}
            showSsr={false}
            showDiscounts={true}
            showInfo={false}
            showDivider={true}
            showTravelerSeparate={true}
            bookedItems={filteredBookedItems}
            isCollapsible
            activeCollapsibleIndexes={openedCollapsibles}
            onCollapseToggle={onCollapseToggle}
            {...(isFlightPackage && { customLabelsMapper: customLabelsFlightMapper })}
            {...(!isConfirmationPage &&
              isBusPackage && { customLabelsMapper: customLabelsBusMapper })}
            {...(isConfirmationPage &&
              isBusPackage && {
                customItemRegistry: {
                  [ApiItemType.BUS]: { Summary: EmptyBusItem }
                } as CustomItemRegistry
              })}
            customPerTravelerSummaryItem={customPerTravelerSummaryItem}
            order={{
              Bus: ['TITLE']
            }}
            customFlightDateDisplay={
              store.packageType ?? sessionStorage.getItem('packageType') === PackageTypes.PFLUG
                ? flight => (
                    <CustomFlightSummaryRendering
                      key={flight.id}
                      item={flight}
                      booking={booking}
                      bookingService={bs}
                      initialInBoundFlight={initialInBoundFlight()}
                    />
                  )
                : undefined
            }
            customPriceModifier={customPriceSummaryRendering}
            showNumberOfPerson
          />
        </section>
        {insuranceBookedItem && (
          <OverlayContainer>
            <div className="insurance__wrapper mt-5">
              <Row noGutters>
                <Col
                  xs={insuranceBookedItem.price.finalPrice > 0 ? 8 : 12}
                  className="align-self-center"
                >
                  <div className="d-flex">
                    <img
                      className="insurance-icon"
                      src={
                        insuranceBookedItem.price.finalPrice > 0
                          ? `${baseUrl}/img/booking_insurance.svg`
                          : `${baseUrl}/img/no_insurance.svg`
                      }
                      alt={
                        insuranceBookedItem.price.finalPrice > 0
                          ? 'an icon for a selected insurance'
                          : 'an icon indicating no insurance was selected'
                      }
                    />
                    <span className="font-weight-bold">{t(Keys.insuranceYourInsurance)}</span>
                  </div>
                </Col>
                {insuranceBookedItem.price.finalPrice > 0 && (
                  <Col xs={4} className="text-right align-self-center">
                    <Price
                      className="summary__traveler__price"
                      price={insuranceBookedItem.price}
                      removeZeroDecimals
                      currencyFirst
                    />
                  </Col>
                )}
              </Row>
              <Row noGutters>
                <Col>
                  {insuranceBookedItem.price.finalPrice > 0
                    ? insuranceBookedItem.description
                    : t(Keys.insuranceNoInsuranceSelected)}
                </Col>
              </Row>
            </div>
          </OverlayContainer>
        )}
        {!!productIBEMode?.serviceCode && showPromotionCode ? (
          <OverlayContainer>
            <div className="prom-code__wrapper mt-5">
              <PromotionCode
                promotionCode={promotionCode}
                setPromotionCode={setPromotionCode}
                onSubmit={handlePromotionCodeSubmit}
                packageCodes={[{ code: productIBEMode.serviceCode, groupSize: NORMAL_GROUP }]}
                startDate={bs.booking?.travelStartDate}
                endDate={bs.booking?.travelEndDate}
                smallMode
              />
            </div>
            {bs.isBusy ? <Overlay /> : <></>}
          </OverlayContainer>
        ) : (
          <></>
        )}
        <div className="sticky-total-price d-flex justify-content-between justify-content-lg-between ">
          <b className="price">{tSummary(KeysSummary.total)}</b>
          <b className="price text-left text-lg-left d-flex d-lg-block">
            <Price
              price={ApiPriceFromJSON({
                finalPrice: totalPrice,
                currencyCode: currencyCode,
                modifiers: []
              })}
              currencyDisplay="symbol"
              className="summary__price"
              removeZeroDecimals
              currencyFirst={true}
              useGrouping={false}
            />
          </b>
        </div>
      </div>
    );
  }
);

const CustomFlightSummaryRendering = ({
  booking,
  item,
  bookingService,
  initialInBoundFlight
}: {
  booking: ApiBooking;
  item: ApiFlightItem;
  bookingService: BookingService;
  initialInBoundFlight: ApiFlightLeg; // <- this is needed when the customer chooses to change the duration after having
  // gone through all steps when using an extension hotel and changes back to the original flight
}): JSX.Element => {
  // we have 2 (or more if connected flights) booked items but want to summarize them to one and hide intermediate fights.
  // hence we want this to render only once
  const selectedItems = useExtendedBookingForPriceOverview();

  const flightItemIds = booking.bookedItems
    .filter(
      bookedItem =>
        bookedItem.itemType === ApiItemType.FLIGHT &&
        bookedItem.componentType === ApiComponentType.REQUIRED
    )
    .map(bitem => bitem.idParent);

  if (item.id !== flightItemIds[0]) return <></>;

  const config = useConfig();
  const appService = useAppService();

  const outBoundFlights = (booking.items
    .filter(
      flightItem =>
        flightItem.itemType === ApiItemType.FLIGHT &&
        (flightItem as ApiFlightItem).segment[0].direction === ApiDirection.OUTBOUND &&
        flightItemIds.includes(flightItem.id)
    )
    .sort(
      (a, b) =>
        new Date((a as ApiFlightItem).segment[0].departure).getTime() -
        new Date((b as ApiFlightItem).segment[0].departure).getTime()
    ) as ApiFlightItem[]) as ApiFlightItem[];

  const inBoundFlights = booking.items
    .filter(
      flightItem =>
        flightItem.itemType === ApiItemType.FLIGHT &&
        (flightItem as ApiFlightItem).segment[0].direction === ApiDirection.INBOUND &&
        flightItemIds.includes(flightItem.id)
    )
    .sort(
      (a, b) =>
        new Date((a as ApiFlightItem).segment[0].departure).getTime() -
        new Date((b as ApiFlightItem).segment[0].departure).getTime()
    ) as ApiFlightItem[];

  if (!outBoundFlights?.length || !inBoundFlights?.length) return <></>;
  else {
    const originLeg = outBoundFlights[0].segment[0].legs[0];
    let destinationLeg = initialInBoundFlight;

    const { duration } = selectedItems || {};
    const { extensionDuration } = duration || {};

    const isExtensionSelected = duration && extensionDuration;
    // When an extension is selected the inbound flight will change, hence the flight overview shall be changed accordingly
    const packageDetails = bookingService.packageCart?.packageModel.packageDetails || [];
    if (packageDetails.length > 0 && isExtensionSelected) {
      const flightComponents = packageDetails
        .flatMap(detail => detail.components)
        .filter(comp => comp.itemType === ApiItemType.FLIGHT);

      const inBoundExtensionFlight = flightComponents
        .filter(flight => flight.componentType === ApiComponentType.REQUIRED)
        .at(-1);
      const selectableItems =
        (inBoundExtensionFlight?.selectableItems.at(-1) as ApiFlightItem) ?? null;

      destinationLeg = selectableItems?.segment[0].legs[0] ?? destinationLeg;
    }

    return (
      <>
        <div className="custom-summary-flight-date-display">
          <div>
            {originLeg && (
              <DateDisplay
                utc
                date={originLeg.departure}
                format={config.displayFormatDate[appService.lang.replace('-', '_')]}
              />
            )}
          </div>
          <div>
            <div>
              <span>{outBoundFlights[0].segment[0]?.origin.code}</span>
            </div>
            <span></span>
            <div>
              <span>
                {outBoundFlights[outBoundFlights.length - 1]?.segment[0]?.destination.code}
              </span>
            </div>
          </div>
        </div>
        <div className="custom-summary-flight-date-display">
          <div>
            {destinationLeg && (
              <DateDisplay
                utc
                date={destinationLeg.departure}
                format={config.displayFormatDate[appService.lang.replace('-', '_')]}
              />
            )}
          </div>
          <div>
            <div>
              <span>{inBoundFlights[0].segment[0]?.origin.code}</span>
            </div>
            <span></span>
            <div>
              <span>{inBoundFlights[inBoundFlights.length - 1].segment[0]?.destination.code}</span>
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default SummaryContent;

export const summaryOverviewSession = new SessionStorage<{
  collapsible: {
    expandedItemIndexes: number[];
  };
}>(summaryOverviewSessionKey);

export const filterBookedItems = (bookedItems: ApiBookedItem[] | null) => {
  if (!bookedItems) return [];

  const flights = bookedItems.filter(
    (item: ApiBookedItem) =>
      item.itemType === ApiItemType.FLIGHT && item.componentType === ApiComponentType.REQUIRED
  );
  const busRides = bookedItems.filter(
    (item: ApiBookedItem) =>
      item.itemType === ApiItemType.BUS && item.componentType === ApiComponentType.REQUIRED
  );

  const filteredHotelItems = bookedItems.filter(
    (item: ApiBookedItem) =>
      (item.itemType === ApiItemType.HOTEL &&
        ((bookedItems.some(
          (innerItem: ApiBookedItem) =>
            innerItem.itemType === ApiItemType.HOTEL &&
            innerItem.componentType === ApiComponentType.OPTIONAL
        ) &&
          item.componentType === ApiComponentType.OPTIONAL) ||
          (!bookedItems.some(
            (innerItem: ApiBookedItem) =>
              innerItem.itemType === ApiItemType.HOTEL &&
              innerItem.componentType === ApiComponentType.OPTIONAL
          ) &&
            item.componentType === ApiComponentType.MAIN))) ||
      (item.componentType === ApiComponentType.MAIN && item.itemType == ApiItemType.HOTEL)
  );

  const filteredRequiredItems = bookedItems.filter(
    (item: ApiBookedItem) =>
      item.componentType === ApiComponentType.REQUIRED && item.itemType == ApiItemType.EXTRA
  );

  // Seperated into 2 arrays to have the optional items explicitly below the required items
  const filteredOptionalItems = bookedItems.filter(
    (item: ApiBookedItem) =>
      (item.componentType === ApiComponentType.OPTIONAL && item.itemType == ApiItemType.FLIGHT) ||
      (item.componentType === ApiComponentType.OPTIONAL && item.itemType == ApiItemType.EXTRA)
  );
  return [
    ...filteredHotelItems,
    ...busRides,
    ...flights,
    ...filteredRequiredItems,
    ...filteredOptionalItems
  ];
};
